import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Grid, theme, Result } from "antd";
import { LinkOutlined } from "@ant-design/icons";
import { fetchPublicShare } from "./api";
import type { MediaEntry, PublicShareItem, PublicShareView } from "./types";
import { MediaThumbTile } from "./MediaThumbTile";
import DualcamPlayerModal from "./DualcamPlayerModal";
import { EmptyState, ErrorState, LoadingState, PageHeader } from "@dowty/ui";
import { AppLegalFooter } from "../legal";
import { isNetworkError } from "@dowty/core";

const { useBreakpoint } = Grid;

/** Adapts a share's item to the shape the existing tile/player components already render. A shared item
 *  is always standalone — the backend never reconstitutes DualCam pairs into a public share — so the
 *  front-camera and location fields are simply absent rather than being invented here. */
const toMediaEntry = (item: PublicShareItem): MediaEntry => ({
    id: String(item.id),
    // "pair" when both DualCam halves were shared together — that's what makes the player show them
    // side by side rather than as one lone camera.
    kind: item.kind === "pair" ? "pair" : "solo",
    mediaType: item.mediaType,
    timestamp: item.timestamp,
    thumbnailUrl: item.thumbnailUrl,
    frontThumbnailUrl: item.frontThumbnailUrl ?? null,
    backUrl: item.fullUrl,
    frontUrl: item.frontUrl ?? null,
    durationMs: item.durationMs,
    latitude: null,
    longitude: null,
    width: item.width,
    height: item.height,
    // A public visitor is shown no attribution at all: the share payload deliberately carries no
    // filenames or contributor names, and inventing one here would put a real person's username on a
    // page anyone holding the link can open.
    contributor: "",
    originalContributor: "",
});

const formatExpiry = (iso: string): string => {
    const expires = new Date(iso);
    if (Number.isNaN(expires.getTime())) return "";
    const hoursLeft = Math.round((expires.getTime() - Date.now()) / 3_600_000);
    if (hoursLeft <= 0) return "expires shortly";
    if (hoursLeft < 24) return `expires in ${hoursLeft} hour${hoursLeft === 1 ? "" : "s"}`;
    const days = Math.round(hoursLeft / 24);
    return `expires in ${days} day${days === 1 ? "" : "s"}`;
};

/**
 * The page someone lands on when a dowtex user sends them a share link. No login, no navigation into
 * the rest of the app, and nothing here reveals which album the items came from or what else is in it —
 * a visitor sees exactly the items that were shared with them and nothing more.
 */
const PublicSharePage: React.FC = () => {
    const isGerman = navigator.language.toLowerCase().startsWith("de");
    const { shareToken } = useParams<{ shareToken: string }>();
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const { token: designToken } = theme.useToken();

    const [share, setShare] = useState<PublicShareView | null>(null);
    // Kept apart from a generic error: an expired or revoked link is the expected end of a share's life,
    // not a fault, and deserves different wording. The backend can't distinguish expired from unknown
    // (deliberately, so tokens can't be probed), so neither can this.
    const [dead, setDead] = useState(false);
    const [failed, setFailed] = useState<string | null>(null);
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        if (!shareToken) return;
        setShare(null);
        setDead(false);
        setFailed(null);
        fetchPublicShare(shareToken)
            .then(setShare)
            .catch(err => {
                if ((err as Error).message === "expired") setDead(true);
                else if (isNetworkError(err)) setFailed(isGerman ? "Keine Verbindung. Bitte erneut versuchen." : "Connection failed. Please retry.");
                else setFailed((err as Error).message);
            });
    }, [shareToken, reloadKey, isGerman]);

    const items = useMemo(() => (share?.items ?? []).map(toMediaEntry), [share]);
    const cardSize = isMobile ? 150 : 190;

    // This page renders outside AuthenticatedLayout (see App.tsx), so it inherits none of the app's
    // chrome — including its background. Without this it's a bare white sheet regardless of the user's
    // theme; the tokens follow whatever ConfigProvider resolved, and the dotted gradient is the same one
    // the signed-in layout paints.
    const surface = (content: React.ReactNode) => (
        <div className="sf-page-content sf-public-page" style={{
            display: "flex",
            flexDirection: "column",
            minHeight: "100vh",
            background: designToken.colorBgLayout,
            backgroundImage: `radial-gradient(circle, ${designToken.colorPrimaryBorder} 1px, transparent 1px)`,
            backgroundSize: "28px 28px",
            color: designToken.colorText,
        }}>
            <main className="sf-content-column" style={{ padding: isMobile ? "16px" : "32px 24px" }}>
                {content}
            </main>
            <AppLegalFooter />
        </div>
    );

    if (dead) {
        return surface(
            <Result
                icon={<LinkOutlined style={{ color: designToken.colorTextQuaternary, fontSize: 56 }} />}
                title={<h1 style={{ margin: 0 }}>{isGerman ? "Dieser Link funktioniert nicht mehr" : "This link no longer works"}</h1>}
                subTitle="Shared links expire after a while, and whoever sent it can also turn it off early. Ask them for a new one."
            />
        );
    }

    if (failed) {
        return surface(<><h1>{isGerman ? "Link konnte nicht geöffnet werden" : "Couldn't open this link"}</h1><ErrorState message={failed} retry={() => setReloadKey(key => key + 1)} retryLabel={isGerman ? "Erneut versuchen" : "Retry"} /></>);
    }

    return surface(
        <>
            <PageHeader
                title="Shared with you"
                subtitle={share && `${share.items.length} ${share.items.length === 1 ? "item" : "items"} · ${formatExpiry(share.expiresAtUtc)}`}
            />

            {share === null && (
                <LoadingState variant="grid" label="Loading shared items" />
            )}
            {share !== null && items.length === 0 && (
                <EmptyState title="Nothing was shared in this link." />
            )}
            {share !== null && items.length > 0 && (
                <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fill, minmax(${cardSize}px, 1fr))`, gap: 10 }}>
                    {items.map((entry, index) => (
                        <MediaThumbTile
                            key={`${entry.id}-${index}`}
                            entry={entry}
                            borderRadius={designToken.borderRadius}
                            onClick={() => setSelectedIndex(index)}
                        />
                    ))}
                </div>
            )}

            {/* No token/albumId passed: that's what gates the modal's download button, and a public
                visitor is view-only. */}
            <DualcamPlayerModal items={items} index={selectedIndex} onIndexChange={setSelectedIndex} />
        </>
    );
};

export default PublicSharePage;
